import { useState, useEffect, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  DialogActions,
  Button,
  CircularProgress,
  type DialogProps,
  type ButtonProps,
} from '@mui/material';
import { ResponsiveDialog } from '@/components/common/ResponsiveDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { DialogHeader } from '@/components/common/DialogHeader';

interface FormDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  isDirty: boolean;

  headerIcon?: ReactNode;
  headerExtra?: ReactNode;

  onSubmit?: () => void;
  submitLabel?: string;
  cancelLabel?: string;
  isLoading?: boolean;
  submitDisabled?: boolean;
  submitColor?: ButtonProps['color'];
  submitIcon?: ReactNode;

  renderActions?: (helpers: { onClose: () => void; isLoading: boolean }) => ReactNode;

  maxWidth?: DialogProps['maxWidth'];
  fullWidth?: boolean;
  children: ReactNode;
}

export function FormDialog({
  open,
  onClose,
  title,
  isDirty,
  headerIcon,
  headerExtra,
  onSubmit,
  submitLabel,
  cancelLabel,
  isLoading = false,
  submitDisabled = false,
  submitColor = 'primary',
  submitIcon,
  renderActions,
  maxWidth = 'sm',
  fullWidth = true,
  children,
}: FormDialogProps) {
  const { t: tc } = useTranslation('common');
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);

  useEffect(() => {
    if (!open) setShowDiscardConfirm(false);
  }, [open]);

  const handleClose = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = () => {
    setShowDiscardConfirm(false);
    onClose();
  };

  return (
    <ResponsiveDialog
      mobileTitle={title}
      open={open}
      onClose={handleClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
    >
      <DialogHeader
        title={title}
        icon={headerIcon}
        extra={headerExtra}
        onClose={handleClose}
        closeDisabled={isLoading}
      />

      {children}

      {renderActions ? (
        renderActions({ onClose: handleClose, isLoading })
      ) : (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            {cancelLabel ?? tc('cancel')}
          </Button>
          {onSubmit && (
            <Button
              variant="contained"
              onClick={onSubmit}
              disabled={isLoading || submitDisabled}
              color={submitColor}
              startIcon={
                isLoading ? (
                  <CircularProgress size={18} color="inherit" />
                ) : (
                  submitIcon
                )
              }
            >
              {isLoading ? tc('saving') : (submitLabel ?? tc('save'))}
            </Button>
          )}
        </DialogActions>
      )}

      <ConfirmDialog
        open={showDiscardConfirm}
        title={tc('unsavedChanges')}
        message={tc('unsavedChangesMessage')}
        confirmText={tc('discard')}
        confirmColor="error"
        onConfirm={handleConfirmDiscard}
        onCancel={() => { setShowDiscardConfirm(false); }}
      />
    </ResponsiveDialog>
  );
}
